import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Glasses, Trophy, TrendingDown, RotateCcw, MousePointer2, Activity, DollarSign, Layers, Link2, Link2Off, RefreshCw, Zap, Menu, X, Percent, Play, Pause, ArrowLeft, Sparkles, Columns2, Award, Compass, PanelLeftClose, PanelLeftOpen, Volume2, VolumeX } from 'lucide-react';
import { useSimStore, computeAnticip } from '../state/store';
import { audio } from '../audio/audioEngine';
import { LOBE_COLORS } from '../scene/anatomy';
import { setSync } from '../scene/viewState';

const stateColor = (a) => (a < 0.34 ? '#00f0ff' : a < 0.67 ? '#ffb800' : '#ff3b30');
const stateLabel = (a) => (a < 0.34 ? 'CALM · BASELINE' : a < 0.67 ? 'ANTICIPATION · RISING' : 'HIGH RISK · dlPFC STRAIN');
const clamp01 = (v) => Math.max(0, Math.min(1, v));

const LOBES = [
  { key: 'f', name: 'Frontal', color: LOBE_COLORS.frontal },
  { key: 'p', name: 'Parietal', color: LOBE_COLORS.parietal },
  { key: 't', name: 'Temporal', color: LOBE_COLORS.temporal },
  { key: 'o', name: 'Occipital', color: LOBE_COLORS.occipital },
];

const INSIGHTS = [
  {
    title: 'Sagittal · Side View',
    body: 'The lateral surface exposes the fronto-parietal executive axis and the temporal lobe below the Sylvian fissure.',
    cards: [
      { name: 'Frontal · dlPFC', role: 'Impulse control & risk appraisal', color: LOBE_COLORS.frontal },
      { name: 'Parietal', role: 'Salience & attention to stakes', color: LOBE_COLORS.parietal },
      { name: 'Temporal', role: 'Memory of past outcomes', color: LOBE_COLORS.temporal },
    ],
  },
  {
    title: 'Coronal · Front Slice',
    body: 'The coronal cut reveals the deep limbic reward core — the accumbens, amygdala and the VTA dopamine origin.',
    cards: [
      { name: 'Nucleus Accumbens', role: 'Dopamine reward hub', color: '#ffd700' },
      { name: 'Amygdala', role: 'Threat & loss response', color: '#ff6b6b' },
      { name: 'VTA', role: 'Dopamine source', color: '#c9a2ff' },
    ],
  },
  {
    title: 'Superior · Top View',
    body: 'Looking down on both hemispheres shows the symmetric dorsal cortex split by the interhemispheric fissure.',
    cards: [
      { name: 'Frontal Poles', role: 'Planning the wager', color: LOBE_COLORS.frontal },
      { name: 'Parietal', role: 'Spatial value integration', color: LOBE_COLORS.parietal },
      { name: 'Occipital', role: 'Visual confirmation', color: LOBE_COLORS.occipital },
    ],
  },
];

const OUTCOME_CARD = {
  clutch: { name: 'dlPFC Override', role: 'Executive control failing under stress', color: '#ff3b30' },
  win: { name: 'Nucleus Accumbens', role: 'Dopamine surge — euphoric reward', color: '#ffd700' },
  loss: { name: 'Amygdala', role: 'Threat response — loss aversion & tilt', color: '#ff3b30' },
};

// Narrative placards shown during the guided intro tour (8s auto-orbit).
const TOUR_CAPTIONS = [
  { title: 'The Neuro-Betting Gallery', body: 'Three anatomically accurate brains, rendered live — watch cognition react to risk.' },
  { title: 'The Executive Cortex', body: 'The frontal dlPFC weighs the wager while the limbic core craves the reward.' },
  { title: 'Reward & Threat', body: 'A win floods the Nucleus Accumbens in gold; a loss ignites the Amygdala in crimson.' },
];

// Guided Neuro-Tour: a narrated walkthrough of each lobe, finishing on the
// reward-vs-threat contrast. hover values map to anatomy REGION ids; 9 = all limbic.
const NEURO_STEPS = [
  { focus: null, hover: 0, cue: 523, title: 'The Neuro-Betting Gallery', body: 'One brain, three views — follow how a single wager ripples through living tissue.' },
  { focus: 0, hover: 1, cue: 587, title: 'Frontal Lobe', body: 'The executive control center. It appraises the risk and tries to reason with the wager.' },
  { focus: 0, hover: 2, cue: 659, title: 'Parietal Lobe', body: 'Where the numbers collide — stake, odds and the size of the potential payout.' },
  { focus: 0, hover: 3, cue: 698, title: 'Temporal Lobe', body: 'Memory of past wins and near-misses fuels the pull to place one more bet.' },
  { focus: 0, hover: 4, cue: 784, title: 'Occipital Lobe', body: 'Vision — the flashing lights, the moving lines and the thrill of the reveal.' },
  { focus: 1, hover: 9, cue: 880, title: 'The Limbic Core', body: 'Beneath the cortex lies the true engine of betting: the reward and threat circuits.' },
  { compare: true, hover: 0, cue: 988, title: 'Big Win vs Heartbreaking Loss', body: 'A win floods the Nucleus Accumbens with dopamine — a loss fires the Amygdala’s threat alarm.' },
];

// Live per-lobe firing meter. Self-contained rAF state so it never re-renders
// the parent HUD.
function LobeMeter() {
  const [act, setAct] = useState({ f: 0.14, p: 0.14, t: 0.2, o: 0.2 });
  useEffect(() => {
    let raf;
    const loop = () => {
      const s = useSimStore.getState();
      const anticip = computeAnticip(s.wager, s.parlay);
      const now = performance.now() / 1000;
      let winA = 0;
      let lossA = 0;
      if (s.event !== 'none') {
        const et = now - s.eventStart;
        if (s.event === 'win') winA = Math.max(0, 1 - et / 2.8);
        else lossA = Math.max(0, 1 - et / 1.4);
      }
      const fl = (seed) => Math.sin(now * 3 + seed) * 0.05;
      const h = s.hoveredRegion;
      const clutch = s.event === 'clutch';
      const cf = clutch ? Math.abs(Math.sin(now * 11)) : 0;
      setAct({
        f: clamp01(0.14 + anticip * 0.8 + (h === 1 ? 0.3 : 0) + winA * 0.2 + fl(1) + clutch * (0.7 + cf * 0.3)),
        p: clamp01(0.14 + anticip * 0.85 + (h === 2 ? 0.3 : 0) + fl(2) + clutch * 0.75),
        t: clamp01(0.2 + (h === 3 ? 0.4 : 0) + lossA * 0.6 + fl(3) + clutch * 0.45),
        o: clamp01(0.2 + (h === 4 ? 0.4 : 0) + winA * 0.75 + fl(4) + clutch * 0.45),
      });
      raf = requestAnimationFrame(loop);
    };
    loop();
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div
      data-testid="lobe-meter"
      className="pointer-events-auto backdrop-blur-xl bg-black/70 border border-white/15 ring-1 ring-black/30 rounded-2xl px-4 py-3 w-full"
    >
      <div className="font-data text-[9px] tracking-[0.28em] uppercase text-white/45 mb-2.5">Lobe Activation</div>
      <div className="flex flex-col gap-2">
        {LOBES.map((l) => (
          <div key={l.key} className="flex items-center gap-2.5">
            <span className="font-data text-[9px] uppercase tracking-wider text-white/60 w-16">{l.name}</span>
            <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
              <div
                className="h-full rounded-full transition-[width] duration-100 ease-out"
                style={{
                  width: `${Math.round(act[l.key] * 100)}%`,
                  backgroundColor: l.color,
                  boxShadow: `0 0 8px ${l.color}`,
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Consecutive win/loss streak tracker. Subscribes to the store; escalation of
// the actual 3D stress visuals is driven from the scene.
function StreakMeter() {
  const type = useSimStore((s) => s.streakType);
  const count = useSimStore((s) => s.streakCount);
  const isWin = type === 'win';
  const color = !type ? '#8b98a8' : isWin ? '#ffd700' : '#ff3b30';
  const pips = Math.min(count, 8);
  return (
    <div
      data-testid="streak-meter"
      className="pointer-events-auto backdrop-blur-xl bg-black/70 border border-white/15 ring-1 ring-black/30 rounded-2xl px-4 py-3 w-full"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="font-data text-[9px] tracking-[0.28em] uppercase text-white/45">Streak</span>
        <span data-testid="streak-label" className="font-data text-[10px] uppercase tracking-widest" style={{ color }}>
          {!type ? 'None' : `${isWin ? 'Win' : 'Loss'} ×${count}`}
        </span>
      </div>
      <div className="flex items-center gap-1">
        {Array.from({ length: 8 }).map((_, i) => (
          <span
            key={i}
            className="flex-1 h-1.5 rounded-full transition-all duration-200"
            style={{
              backgroundColor: i < pips ? color : 'rgba(255,255,255,0.1)',
              boxShadow: i < pips ? `0 0 8px ${color}` : 'none',
            }}
          />
        ))}
      </div>
    </div>
  );
}

export default function HUD({ xrStore }) {
  const wager = useSimStore((s) => s.wager);
  const parlay = useSimStore((s) => s.parlay);
  const fps = useSimStore((s) => s.fps);
  const quality = useSimStore((s) => s.quality);
  const event = useSimStore((s) => s.event);
  const setWager = useSimStore((s) => s.setWager);
  const setParlay = useSimStore((s) => s.setParlay);
  const triggerWin = useSimStore((s) => s.triggerWin);
  const triggerLoss = useSimStore((s) => s.triggerLoss);
  const triggerClutch = useSimStore((s) => s.triggerClutch);
  const syncViews = useSimStore((s) => s.syncViews);
  const setSyncViews = useSimStore((s) => s.setSyncViews);
  const triggerReset = useSimStore((s) => s.triggerReset);
  const sliceDepth = useSimStore((s) => s.sliceDepth);
  const setSliceDepth = useSimStore((s) => s.setSliceDepth);
  const clutchWinOdds = useSimStore((s) => s.clutchWinOdds);
  const setClutchWinOdds = useSimStore((s) => s.setClutchWinOdds);
  const focusIndex = useSimStore((s) => s.focusIndex);
  const setFocus = useSimStore((s) => s.setFocus);
  const sliceSweep = useSimStore((s) => s.sliceSweep);
  const toggleSliceSweep = useSimStore((s) => s.toggleSliceSweep);
  const tourActive = useSimStore((s) => s.tourActive);
  const setTour = useSimStore((s) => s.setTour);
  const compareMode = useSimStore((s) => s.compareMode);
  const setCompare = useSimStore((s) => s.setCompare);
  const milestone = useSimStore((s) => s.milestone);
  const clearMilestone = useSimStore((s) => s.clearMilestone);
  const milestoneStart = useSimStore((s) => s.milestoneStart);
  const neuroTour = useSimStore((s) => s.neuroTour);
  const sidebarOpen = useSimStore((s) => s.sidebarOpen);
  const toggleSidebar = useSimStore((s) => s.toggleSidebar);
  const isMuted = useSimStore((s) => s.isMuted);
  const toggleMute = useSimStore((s) => s.toggleMute);
  const neuroStep = useSimStore((s) => s.neuroStep);
  const startNeuroTour = useSimStore((s) => s.startNeuroTour);
  const stopNeuroTour = useSimStore((s) => s.stopNeuroTour);
  const setNeuroStep = useSimStore((s) => s.setNeuroStep);
  const applyTourStep = useSimStore((s) => s.applyTourStep);

  const [tourCaption, setTourCaption] = useState(0);
  useEffect(() => {
    if (!tourActive) return;
    setTourCaption(0);
    const timers = [
      setTimeout(() => setTourCaption(1), 2700),
      setTimeout(() => setTourCaption(2), 5400),
    ];
    return () => timers.forEach(clearTimeout);
  }, [tourActive]);

  const [vrMsg, setVrMsg] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [showTourPrompt, setShowTourPrompt] = useState(false);
  const [isDesktop, setIsDesktop] = useState(typeof window !== 'undefined' ? window.innerWidth >= 1024 : true);

  useEffect(() => {
    const mq = window.matchMedia('(min-width:1024px)');
    const on = () => setIsDesktop(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);

  const anticip = computeAnticip(wager, parlay);
  const accent = stateColor(anticip);

  useEffect(() => {
    audio.setIntensity(anticip);
  }, [anticip]);

  const start = () => {
    try {
      audio.ensure();
      audio.resume();
      audio.setMuted(useSimStore.getState().isMuted);
    } catch (e) {
      /* audio unavailable — visuals continue */
    }
  };

  const onWin = () => {
    start();
    triggerWin();
    try {
      audio.win(useSimStore.getState().streakCount >= 3);
    } catch (e) {}
  };
  const onLoss = () => {
    start();
    triggerLoss();
    try {
      audio.loss();
    } catch (e) {}
  };
  const onEnterVR = async () => {
    start();
    try {
      await xrStore.enterVR();
    } catch (e) {
      setVrMsg('WebXR VR not available on this device / browser');
      setTimeout(() => setVrMsg(''), 3200);
    }
  };

  const toggleSync = () => {
    const next = !syncViews;
    setSyncViews(next);
    setSync(next);
  };
  const onResetViews = () => {
    triggerReset();
  };

  const rumble = () => {
    try {
      const st = xrStore && xrStore.getState ? xrStore.getState() : null;
      const session = st && st.session;
      if (!session) return;
      const pulseOnce = () => {
        for (const src of session.inputSources) {
          const gp = src && src.gamepad;
          const act = gp && gp.hapticActuators && gp.hapticActuators[0];
          if (act && act.pulse) act.pulse(0.85, 140);
        }
      };
      let n = 0;
      const id = setInterval(() => {
        pulseOnce();
        if (++n > 8) clearInterval(id);
      }, 560);
    } catch (e) {}
  };

  const onClutch = () => {
    if (event !== 'none') return;
    start();
    triggerClutch();
    try {
      audio.clutch();
    } catch (e) {}
    rumble();
  };

  const onToggleCompare = () => {
    start();
    setCompare(!compareMode);
  };

  const onToggleMute = () => {
    const next = !isMuted;
    toggleMute();
    try {
      audio.ensure();
      audio.resume();
      audio.setMuted(next);
    } catch (e) {}
  };

  const renderAudioBtn = (testid, extraClass) => (
    <button
      data-testid={testid}
      onClick={onToggleMute}
      className={`pointer-events-auto flex items-center gap-2 rounded-xl border transition-colors active:scale-95 ${extraClass}`}
      style={{
        backgroundColor: isMuted ? 'rgba(255,80,80,0.16)' : 'rgba(0,224,208,0.16)',
        borderColor: isMuted ? 'rgba(255,80,80,0.45)' : 'rgba(0,224,208,0.45)',
        color: isMuted ? '#ff6b6b' : '#00e0d0',
      }}
      title={isMuted ? 'Audio muted — click to unmute' : 'Audio on — click to mute'}
    >
      {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
      <span className="font-data text-[11px] font-bold uppercase tracking-widest whitespace-nowrap">Audio: {isMuted ? 'Off' : 'On'}</span>
    </button>
  );

  const onStartNeuroTour = () => {
    start();
    setMenuOpen(false);
    setShowTourPrompt(false);
    try { localStorage.setItem('neuroTourSeen', '1'); } catch (e) {}
    startNeuroTour();
  };

  // Drive the guided Neuro-Tour: apply each step (camera focus / highlight /
  // compare) + play its spatial cue, then auto-advance. Manual prev/next simply
  // change neuroStep, re-running this effect.
  useEffect(() => {
    if (!neuroTour) return;
    const step = NEURO_STEPS[neuroStep];
    applyTourStep(step);
    try {
      audio.ensure();
      audio.resume();
      audio.cue(step.cue);
    } catch (e) {}
    const isLast = neuroStep >= NEURO_STEPS.length - 1;
    const id = setTimeout(() => {
      if (isLast) stopNeuroTour();
      else setNeuroStep(neuroStep + 1);
    }, isLast ? 8500 : 5400);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [neuroTour, neuroStep]);

  // First-visit prompt: gently offer the tour once the intro auto-orbit ends.
  useEffect(() => {
    if (tourActive || neuroTour) return;
    if (typeof window === 'undefined') return;
    try {
      if (localStorage.getItem('neuroTourSeen')) return;
    } catch (e) {
      return;
    }
    const id = setTimeout(() => setShowTourPrompt(true), 900);
    return () => clearTimeout(id);
  }, [tourActive, neuroTour]);

  const dismissTourPrompt = () => {
    setShowTourPrompt(false);
    try { localStorage.setItem('neuroTourSeen', '1'); } catch (e) {}
  };

  // 5-win milestone: play the fanfare + rumble, then auto-clear the celebration.
  useEffect(() => {
    if (!milestone) return;
    try {
      audio.ensure();
      audio.resume();
      audio.milestone();
    } catch (e) {}
    rumble();
    const id = setTimeout(() => clearMilestone(), 5000);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [milestone, milestoneStart]);

  // Shared control content — rendered in the desktop deck and the mobile drawer.
  const renderControls = (stack = false) => (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span
            className="w-2.5 h-2.5 rounded-full transition-colors duration-500"
            style={{ backgroundColor: accent, boxShadow: `0 0 12px ${accent}` }}
          />
          <span
            data-testid="neural-state"
            className="font-data text-[10px] md:text-xs uppercase tracking-[0.28em] transition-colors duration-500"
            style={{ color: accent }}
          >
            {stateLabel(anticip)}
          </span>
        </div>
      </div>

      {/* Coronal slice-depth scrubber */}
      <div className="flex items-center gap-3 mb-3">
        <label className="font-data text-[10px] tracking-[0.2em] uppercase text-white/50 whitespace-nowrap w-24">Coronal Slice</label>
        <input
          data-testid="slice-slider"
          className="neuro-range flex-1"
          type="range"
          min={-45}
          max={45}
          step={1}
          value={Math.round(sliceDepth * 100)}
          onChange={(e) => setSliceDepth(Number(e.target.value) / 100)}
          style={{ '--accent': '#63b85a' }}
        />
        <span data-testid="slice-value" className="font-data text-xs tabular-nums text-emerald-300 w-14 text-right">
          {Math.round(((0.45 - sliceDepth) / 0.9) * 100)}%
        </span>
        <button
          data-testid="slice-sweep-button"
          onClick={toggleSliceSweep}
          className="flex items-center justify-center w-8 h-8 rounded-lg border border-white/15 transition-colors"
          style={{ backgroundColor: sliceSweep ? 'rgba(99,184,90,0.22)' : 'transparent', color: sliceSweep ? '#63b85a' : 'rgba(255,255,255,0.55)' }}
          title="Auto-sweep the coronal slice"
        >
          {sliceSweep ? <Pause size={13} /> : <Play size={13} />}
        </button>
      </div>

      {/* Clutch odds dial */}
      <div className="flex items-center gap-3 mb-4">
        <label className="font-data text-[10px] tracking-[0.2em] uppercase text-white/50 whitespace-nowrap w-24 flex items-center gap-1">
          <Percent size={11} /> Clutch Odds
        </label>
        <input
          data-testid="clutch-odds-slider"
          className="neuro-range flex-1"
          type="range"
          min={5}
          max={95}
          step={5}
          value={Math.round(clutchWinOdds * 100)}
          onChange={(e) => setClutchWinOdds(Number(e.target.value) / 100)}
          style={{ '--accent': '#ffd700' }}
        />
        <span data-testid="clutch-odds-value" className="font-data text-xs tabular-nums text-amber-300 w-14 text-right">
          {Math.round(clutchWinOdds * 100)}% W
        </span>
      </div>

      <div className={stack ? 'grid grid-cols-1 gap-4' : 'grid grid-cols-1 lg:grid-cols-[1fr_1fr_auto] gap-6 lg:gap-8 items-end'}>
        {/* Wager slider */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <label className="font-data text-[10px] tracking-[0.2em] uppercase text-white/50 flex items-center gap-1.5">
              <DollarSign size={12} /> Wager Size
            </label>
            <span data-testid="wager-value" className="font-data text-lg tabular-nums text-cyan-300">
              ${wager.toLocaleString()}
            </span>
          </div>
          <input
            data-testid="wager-slider"
            className="neuro-range"
            type="range"
            min={1}
            max={5000}
            step={1}
            value={wager}
            onChange={(e) => {
              start();
              setWager(Number(e.target.value));
            }}
            style={{ '--accent': accent }}
          />
        </div>

        {/* Parlay slider */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <label className="font-data text-[10px] tracking-[0.2em] uppercase text-white/50 flex items-center gap-1.5">
              <Layers size={12} /> Parlay Density
            </label>
            <span data-testid="parlay-value" className="font-data text-lg tabular-nums text-cyan-300">
              {parlay} {parlay === 1 ? 'LEG' : 'LEGS'}
            </span>
          </div>
          <input
            data-testid="parlay-slider"
            className="neuro-range"
            type="range"
            min={1}
            max={12}
            step={1}
            value={parlay}
            onChange={(e) => {
              start();
              setParlay(Number(e.target.value));
            }}
            style={{ '--accent': accent }}
          />
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2.5 lg:min-w-[190px]">
          <div className="grid grid-cols-2 gap-2.5">
            <button
              data-testid="trigger-win-button"
              onClick={onWin}
              className="flex items-center justify-center gap-1.5 px-3 py-3 bg-transparent border border-amber-400/30 text-amber-400 font-data text-[10px] uppercase tracking-widest rounded-lg hover:bg-amber-400/15 hover:border-amber-300 hover:shadow-[0_0_20px_rgba(255,215,0,0.35)] hover:text-amber-200 transition-colors duration-300"
            >
              <Trophy size={13} /> Win
            </button>
            <button
              data-testid="trigger-loss-button"
              onClick={onLoss}
              className="flex items-center justify-center gap-1.5 px-3 py-3 bg-transparent border border-red-500/30 text-red-400 font-data text-[10px] uppercase tracking-widest rounded-lg hover:bg-red-500/15 hover:border-red-400 hover:shadow-[0_0_20px_rgba(255,59,48,0.35)] hover:text-red-300 transition-colors duration-300"
            >
              <TrendingDown size={13} /> Loss
            </button>
          </div>
          <button
            data-testid="trigger-clutch-button"
            onClick={onClutch}
            disabled={event !== 'none'}
            className="flex items-center justify-center gap-2 px-5 py-3 font-data font-bold text-[11px] uppercase tracking-[0.14em] rounded-lg text-white bg-gradient-to-r from-amber-500 to-red-600 hover:from-amber-400 hover:to-red-500 hover:shadow-[0_0_28px_rgba(255,90,40,0.6)] disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-300 animate-pulse"
          >
            <Zap size={15} /> Trigger Clutch Moment
          </button>
          <button
            data-testid="enter-vr-button"
            onClick={onEnterVR}
            className="flex items-center justify-center gap-2 px-5 py-2.5 bg-cyan-500 text-black font-data font-bold text-[11px] uppercase tracking-[0.14em] rounded-lg hover:bg-cyan-400 hover:shadow-[0_0_28px_rgba(0,240,255,0.55)] transition-colors duration-300"
          >
            <Glasses size={15} /> Enter VR
          </button>
          <button
            data-testid="reset-button"
            onClick={() => {
              setWager(100);
              setParlay(1);
            }}
            className="flex items-center justify-center gap-1.5 px-3 py-2 text-white/40 font-data text-[10px] uppercase tracking-widest rounded-lg hover:text-white/70 transition-colors duration-300"
          >
            <RotateCcw size={12} /> Reset Deck
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="fixed inset-0 z-50 pointer-events-none font-body text-white select-none">
      {/* Full-screen Outcome Bloom (Win gold / Loss crimson), fades over 3s */}
      <AnimatePresence>
        {(event === 'win' || event === 'loss') && (
          <motion.div
            key={`bloom-${event}`}
            data-testid="outcome-bloom"
            initial={{ opacity: 0.85 }}
            animate={{ opacity: 0 }}
            transition={{ duration: 3, ease: 'easeOut' }}
            className="absolute inset-0 z-[45]"
            style={{
              background:
                event === 'win'
                  ? 'radial-gradient(circle at 50% 45%, rgba(255,215,0,0.95), rgba(255,150,0,0.5) 40%, rgba(255,120,0,0) 72%)'
                  : 'radial-gradient(circle at 50% 45%, rgba(255,30,30,0.95), rgba(140,0,0,0.55) 42%, rgba(90,0,0,0) 72%)',
              mixBlendMode: 'screen',
            }}
          />
        )}
      </AnimatePresence>

      {/* Milestone Celebration — 5-win streak golden overlay + fanfare banner */}
      <AnimatePresence>
        {milestone && (
          <motion.div
            key="milestone-overlay"
            data-testid="milestone-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.9, 0.55, 0.9, 0] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 5, times: [0, 0.1, 0.5, 0.7, 1], ease: 'easeInOut' }}
            className="absolute inset-0 z-[46]"
            style={{
              background:
                'radial-gradient(circle at 50% 45%, rgba(255,220,90,0.95), rgba(255,170,20,0.45) 45%, rgba(255,150,0,0) 74%)',
              mixBlendMode: 'screen',
            }}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {milestone && (
          <motion.div
            key="milestone-banner"
            data-testid="milestone-banner"
            initial={{ opacity: 0, scale: 0.8, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 1.1 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="pointer-events-none absolute top-[18%] inset-x-0 mx-auto w-fit z-[67] text-center px-6"
          >
            <div className="flex items-center justify-center gap-3 mb-1">
              <Award size={30} className="text-amber-300" />
              <span className="font-placard font-black text-4xl md:text-6xl tracking-tight" style={{ color: '#ffd700', textShadow: '0 0 44px rgba(255,215,0,0.6)' }}>
                5-Win Streak
              </span>
              <Award size={30} className="text-amber-300" />
            </div>
            <div className="font-data text-[11px] md:text-sm tracking-[0.32em] uppercase text-amber-200/80">
              Milestone · Dopamine Cascade Unlocked
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Compare Mode hint labels above each brain slot */}
      <AnimatePresence>
        {compareMode && (
          <motion.div
            key="compare-hint"
            data-testid="compare-hint"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 14 }}
            transition={{ duration: 0.4 }}
            className="pointer-events-none absolute top-[52%] left-0 right-0 px-[20%] z-[66] flex items-start justify-between"
          >
            <div className="text-center">
              <div className="font-data text-[10px] md:text-xs tracking-[0.3em] uppercase" style={{ color: '#ffd700' }}>Win Response</div>
              <div className="font-body text-[10px] md:text-xs text-white/55">Nucleus Accumbens · Reward</div>
            </div>
            <div className="text-center">
              <div className="font-data text-[10px] md:text-xs tracking-[0.3em] uppercase" style={{ color: '#ff3b30' }}>Loss Response</div>
              <div className="font-body text-[10px] md:text-xs text-white/55">Amygdala · Threat</div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Guided Tour narrative captions (gallery placards) */}
      <AnimatePresence mode="wait">
        {tourActive && (
          <motion.div
            key={`tour-cap-${tourCaption}`}
            data-testid="tour-caption"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.5 }}
            className="pointer-events-none absolute top-[calc(12%-40px)] inset-x-0 mx-auto w-[92%] max-w-2xl z-[66] text-center px-7 py-4 rounded-2xl backdrop-blur-xl border border-white/12 shadow-[0_12px_44px_rgba(0,0,0,0.45)]"
            style={{ backgroundColor: 'rgba(8,11,17,0.82)' }}
          >
            <div className="font-placard font-black text-2xl md:text-4xl tracking-tight text-white" style={{ textShadow: '0 2px 24px rgba(0,0,0,0.6)' }}>
              {TOUR_CAPTIONS[tourCaption].title}
            </div>
            <div className="font-body text-sm md:text-base text-white/80 mt-2">{TOUR_CAPTIONS[tourCaption].body}</div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Guided tour skip */}
      <AnimatePresence>
        {tourActive && (
          <motion.button
            data-testid="skip-tour-button"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setTour(false)}
            className="pointer-events-auto absolute top-[26%] inset-x-0 mx-auto block w-fit z-[70] backdrop-blur-xl bg-black/70 border border-white/20 rounded-full px-5 py-2 font-data text-[10px] uppercase tracking-[0.28em] text-white/80 hover:text-white"
          >
            Skip Intro Tour
          </motion.button>
        )}
      </AnimatePresence>

      {/* Guided Neuro-Tour placard + narration + controls */}
      <AnimatePresence>
        {neuroTour && (
          <motion.div
            key={`neuro-${neuroStep}`}
            data-testid="neuro-tour-placard"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="pointer-events-auto absolute inset-x-0 mx-auto top-[11%] z-[68] w-[92%] max-w-xl text-center backdrop-blur-2xl border border-white/15 ring-1 ring-black/50 shadow-[0_16px_60px_rgba(0,0,0,0.5)] rounded-2xl px-6 py-5"
            style={{ backgroundColor: 'rgba(8,11,17,0.94)' }}
          >
            <div className="font-data text-[9px] tracking-[0.34em] uppercase text-cyan-400 mb-2">
              Guided Neuro-Tour · {neuroStep + 1} / {NEURO_STEPS.length}
            </div>
            <div className="font-placard font-black text-2xl md:text-3xl tracking-tight" style={{ textShadow: '0 2px 20px rgba(0,0,0,0.6)' }}>
              {NEURO_STEPS[neuroStep].title}
            </div>
            <div data-testid="neuro-tour-body" className="font-body text-sm md:text-base text-white/90 mt-2 leading-relaxed">
              {NEURO_STEPS[neuroStep].body}
            </div>
            <div className="flex items-center justify-center gap-2 mt-4">
              {NEURO_STEPS.map((_, i) => (
                <span
                  key={i}
                  className="h-1.5 rounded-full transition-all duration-300"
                  style={{ width: i === neuroStep ? 18 : 6, backgroundColor: i === neuroStep ? '#00e0d0' : 'rgba(255,255,255,0.22)' }}
                />
              ))}
            </div>
            <div className="flex items-center justify-center gap-2 mt-4">
              <button
                data-testid="neuro-tour-prev"
                onClick={() => setNeuroStep(Math.max(0, neuroStep - 1))}
                disabled={neuroStep === 0}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full font-data text-[10px] uppercase tracking-widest border border-white/15 text-white/70 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ArrowLeft size={12} /> Back
              </button>
              <button
                data-testid="neuro-tour-skip"
                onClick={stopNeuroTour}
                className="px-4 py-2 rounded-full font-data text-[10px] uppercase tracking-widest text-white/45 hover:text-white/80 transition-colors"
              >
                End Tour
              </button>
              <button
                data-testid="neuro-tour-next"
                onClick={() => (neuroStep >= NEURO_STEPS.length - 1 ? stopNeuroTour() : setNeuroStep(neuroStep + 1))}
                className="px-5 py-2 rounded-full font-data font-bold text-[10px] uppercase tracking-widest text-black bg-cyan-400 hover:bg-cyan-300 transition-colors"
              >
                {neuroStep >= NEURO_STEPS.length - 1 ? 'Finish' : 'Next'}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* First-visit prompt offering the guided tour */}
      <AnimatePresence>
        {showTourPrompt && !neuroTour && (
          <motion.div
            key="neuro-tour-prompt"
            data-testid="neuro-tour-prompt"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="pointer-events-auto absolute inset-x-0 mx-auto top-[13%] z-[69] w-[92%] max-w-md text-center rounded-2xl px-7 py-6 border"
            style={{
              backgroundColor: 'rgba(247,249,252,0.97)',
              backdropFilter: 'blur(28px)',
              WebkitBackdropFilter: 'blur(28px)',
              borderColor: 'rgba(20,35,63,0.14)',
              boxShadow: '0 24px 80px rgba(15,25,45,0.35)',
            }}
          >
            <Compass size={22} className="mx-auto mb-2" style={{ color: '#0e7490' }} />
            <div
              className="font-placard font-black text-xl md:text-2xl tracking-tight mb-1"
              style={{ color: '#14233f', textShadow: '0 1px 3px rgba(20,35,63,0.18)' }}
            >
              First time here?
            </div>
            <div className="font-body text-sm mb-5" style={{ color: '#333333' }}>
              Take a 40-second guided walkthrough of the betting brain — each lobe, then the reward-vs-threat contrast.
            </div>
            <div className="flex items-center justify-center gap-3">
              <button
                data-testid="neuro-tour-prompt-dismiss"
                onClick={dismissTourPrompt}
                className="px-4 py-2.5 rounded-full font-data text-[10px] uppercase tracking-widest hover:opacity-70 transition-opacity"
                style={{ color: '#333333' }}
              >
                Maybe later
              </button>
              <button
                data-testid="neuro-tour-prompt-start"
                onClick={onStartNeuroTour}
                className="flex items-center gap-1.5 px-6 py-2.5 rounded-full font-data font-bold text-[11px] uppercase tracking-widest text-white active:scale-95 transition-transform"
                style={{ backgroundColor: '#0891b2', boxShadow: '0 8px 24px rgba(8,145,178,0.55)' }}
              >
                <Compass size={14} /> Start Tour
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Interactive Focus panel (hidden during the guided Neuro-Tour) */}
      <AnimatePresence>
        {focusIndex !== null && !neuroTour && (
          <motion.div
            key="focus-panel"
            data-testid="focus-panel"
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 40 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="pointer-events-auto absolute top-24 right-4 lg:top-28 lg:right-8 z-[65] w-[300px] max-w-[86vw] backdrop-blur-2xl bg-black/80 border border-white/15 ring-1 ring-black/40 rounded-2xl p-5"
          >
            <div className="flex items-center justify-between mb-1">
              <span className="font-data text-[9px] tracking-[0.3em] uppercase text-cyan-400">Focus Mode</span>
              <button
                data-testid="back-to-gallery-button"
                onClick={() => setFocus(null)}
                className="flex items-center gap-1.5 font-data text-[9px] uppercase tracking-widest text-white/55 hover:text-white"
              >
                <ArrowLeft size={12} /> Gallery
              </button>
            </div>
            <h2 className="font-heading text-lg tracking-tight uppercase mb-2">{INSIGHTS[focusIndex].title}</h2>
            <p className="font-body text-sm text-white/60 leading-relaxed mb-4">{INSIGHTS[focusIndex].body}</p>

            <div className="flex flex-col gap-2 mb-4">
              {INSIGHTS[focusIndex].cards.map((c, i) => (
                <div key={i} className="flex items-start gap-2.5 bg-white/5 rounded-lg px-3 py-2 border border-white/5">
                  <span className="mt-1 w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: c.color, boxShadow: `0 0 8px ${c.color}` }} />
                  <div>
                    <div className="font-data text-[11px] uppercase tracking-wider" style={{ color: c.color }}>{c.name}</div>
                    <div className="font-body text-[11px] text-white/50">{c.role}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Live outcome card (data reveal) */}
            <AnimatePresence>
              {event !== 'none' && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mb-4 overflow-hidden"
                >
                  <div className="font-data text-[8px] tracking-[0.3em] uppercase text-white/40 mb-1.5">Live Neuro-Response</div>
                  <div
                    data-testid="focus-outcome-card"
                    className="flex items-center gap-2.5 rounded-lg px-3 py-2.5 border"
                    style={{ borderColor: `${OUTCOME_CARD[event].color}55`, backgroundColor: `${OUTCOME_CARD[event].color}18` }}
                  >
                    <span className="w-2.5 h-2.5 rounded-full animate-pulse" style={{ backgroundColor: OUTCOME_CARD[event].color, boxShadow: `0 0 10px ${OUTCOME_CARD[event].color}` }} />
                    <div>
                      <div className="font-data text-[11px] uppercase tracking-wider" style={{ color: OUTCOME_CARD[event].color }}>{OUTCOME_CARD[event].name}</div>
                      <div className="font-body text-[11px] text-white/55">{OUTCOME_CARD[event].role}</div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <button
              data-testid="run-simulation-button"
              onClick={onClutch}
              disabled={event !== 'none'}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 font-data font-bold text-[11px] uppercase tracking-[0.14em] rounded-lg text-white bg-gradient-to-r from-cyan-500 to-indigo-600 hover:from-cyan-400 hover:to-indigo-500 hover:shadow-[0_0_24px_rgba(0,224,208,0.5)] disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-300"
            >
              <Sparkles size={15} /> {event === 'none' ? 'Run Simulation' : 'Simulating…'}
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile / tablet top bar (< lg): title + prominent Enter VR + Controls menu */}
      <div className="lg:hidden pointer-events-auto absolute top-0 left-0 right-0 z-[60] flex items-center justify-between gap-2 px-3 py-2 bg-black/75 backdrop-blur-xl border-b border-white/10">
        <div className="min-w-0 flex-shrink">
          <h1 className="font-heading text-sm tracking-tighter uppercase font-light leading-none truncate">Neuro-Betting</h1>
          <p className="font-data text-[8px] tracking-[0.28em] uppercase text-cyan-300/70">{fps} FPS · WebXR</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            data-testid="audio-toggle-mobile"
            onClick={onToggleMute}
            className="flex items-center justify-center min-h-[44px] w-11 rounded-xl border transition-colors active:scale-95"
            style={{
              backgroundColor: isMuted ? 'rgba(255,80,80,0.16)' : 'rgba(0,224,208,0.16)',
              borderColor: isMuted ? 'rgba(255,80,80,0.45)' : 'rgba(0,224,208,0.45)',
              color: isMuted ? '#ff6b6b' : '#00e0d0',
            }}
            title={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
          <button
            data-testid="enter-vr-button-mobile"
            onClick={onEnterVR}
            className="flex items-center gap-1.5 px-4 min-h-[44px] bg-cyan-500 text-black font-data font-bold text-[11px] uppercase tracking-widest rounded-xl shadow-[0_0_18px_rgba(0,240,255,0.5)] active:scale-95 transition-transform"
          >
            <Glasses size={16} /> Enter VR
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMenuOpen((o) => !o)}
            className="flex items-center gap-1.5 px-3 min-h-[44px] bg-white/10 border border-white/15 rounded-xl active:scale-95 transition-transform"
            aria-label="Toggle controls"
          >
            {menuOpen ? <X size={18} /> : <Menu size={18} />}
            <span className="font-data text-[10px] uppercase tracking-widest text-white/80">Controls</span>
          </button>
        </div>
      </div>

      {/* Desktop top navigation (>= lg) */}
      {/* Desktop LEFT SIDEBAR (>= lg): collapsible column — meters + controls */}
      <button
        data-testid="sidebar-toggle"
        onClick={toggleSidebar}
        className="hidden lg:flex pointer-events-auto absolute top-4 z-[80] items-center justify-center w-9 h-9 rounded-lg backdrop-blur-xl bg-black/75 border border-white/15 text-white/70 hover:text-white transition-[left,color] duration-300"
        style={{ left: sidebarOpen ? 296 : 12 }}
        title={sidebarOpen ? 'Collapse panel' : 'Expand panel'}
      >
        {sidebarOpen ? <PanelLeftClose size={16} /> : <PanelLeftOpen size={16} />}
      </button>

      {/* Audio toggle stays reachable even when the sidebar is collapsed */}
      {isDesktop && !sidebarOpen && (
        <div className="absolute top-4 z-[85]" style={{ left: 60 }}>
          {renderAudioBtn('audio-toggle-collapsed', 'px-4 py-2.5 backdrop-blur-xl')}
        </div>
      )}

      <motion.aside
        data-testid="control-sidebar"
        initial={false}
        animate={{ x: sidebarOpen ? 0 : '-108%' }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        className="hidden lg:flex pointer-events-auto absolute top-0 left-0 z-[75] h-full w-[280px] flex-col gap-3 p-4 overflow-y-auto overflow-x-hidden backdrop-blur-2xl bg-black/80 border-r border-white/12 shadow-[8px_0_40px_rgba(0,0,0,0.4)]"
      >
        <div className="backdrop-blur-xl bg-black/50 border border-white/12 rounded-2xl px-4 py-3">
          <h1 className="font-heading text-xl tracking-tighter uppercase font-light leading-none">Neuro-Betting</h1>
          <div className="flex items-center justify-between mt-1.5">
            <p className="font-data text-[9px] tracking-[0.3em] uppercase text-white/40">WebXR Neural Sim</p>
            <span className="flex items-center gap-1.5 font-data text-[10px] tabular-nums text-cyan-300">
              <Activity size={11} /> <span data-testid="fps-indicator">{fps}</span> FPS
            </span>
          </div>
        </div>

        {renderAudioBtn('audio-toggle', 'w-full justify-center px-4 py-3')}

        <LobeMeter />
        <StreakMeter />

        <div className="backdrop-blur-xl bg-black/50 border border-white/12 rounded-2xl p-4">
          {renderControls(true)}
          <AnimatePresence>
            {vrMsg && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="font-data text-[10px] uppercase tracking-widest text-amber-400/80 mt-3 text-center"
              >
                {vrMsg}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="backdrop-blur-xl bg-black/50 border border-white/12 rounded-2xl p-3">
          <span className="font-data text-[9px] tracking-[0.28em] uppercase text-white/40">Gallery Views</span>
          <div className="grid grid-cols-2 gap-2 mt-2.5">
            <button
              data-testid="sync-toggle"
              onClick={toggleSync}
              className="flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg border border-white/12 font-data text-[9px] uppercase tracking-widest transition-colors"
              style={{ backgroundColor: syncViews ? 'rgba(0,224,208,0.16)' : 'transparent', color: syncViews ? '#00e0d0' : 'rgba(255,255,255,0.6)' }}
            >
              {syncViews ? <Link2 size={12} /> : <Link2Off size={12} />} {syncViews ? 'Synced' : 'Indep'}
            </button>
            <button
              data-testid="reset-views-button"
              onClick={onResetViews}
              className="flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg border border-white/12 font-data text-[9px] uppercase tracking-widest text-white/60 hover:text-white transition-colors"
            >
              <RefreshCw size={12} /> Reset
            </button>
            <button
              data-testid="compare-toggle"
              onClick={onToggleCompare}
              className="flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg border border-white/12 font-data text-[9px] uppercase tracking-widest transition-colors"
              style={{ backgroundColor: compareMode ? 'rgba(255,215,0,0.16)' : 'transparent', color: compareMode ? '#ffd700' : 'rgba(255,255,255,0.6)' }}
            >
              <Columns2 size={12} /> {compareMode ? 'Exit' : 'Compare'}
            </button>
            <button
              data-testid="neuro-tour-button"
              onClick={onStartNeuroTour}
              className="flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg border border-white/12 font-data text-[9px] uppercase tracking-widest transition-colors"
              style={{ backgroundColor: neuroTour ? 'rgba(0,224,208,0.16)' : 'transparent', color: neuroTour ? '#00e0d0' : 'rgba(255,255,255,0.6)' }}
            >
              <Compass size={12} /> Tour
            </button>
          </div>
        </div>
      </motion.aside>

      {/* Center resolution banner */}
      <AnimatePresence>
        {event !== 'none' && (
          <motion.div
            key={event}
            data-testid="outcome-banner"
            initial={{ opacity: 0, scale: 0.9, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 1.05 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="pointer-events-none absolute top-[26%] inset-x-0 mx-auto w-fit text-center"
          >
            <motion.div
              animate={event === 'clutch' ? { scale: [1, 1.06, 1] } : {}}
              transition={event === 'clutch' ? { duration: 0.5, repeat: Infinity } : {}}
              className="font-heading text-3xl md:text-5xl tracking-tighter uppercase"
              style={{
                color: event === 'win' ? '#ffd700' : '#ff2a2a',
                textShadow: `0 0 40px ${event === 'win' ? '#ffd70088' : '#ff2a2a88'}`,
              }}
            >
              {event === 'win' ? 'Dopamine Surge' : event === 'clutch' ? 'Clutch Moment' : 'Amygdala Shock'}
            </motion.div>
            <div className="font-data text-[11px] tracking-[0.32em] uppercase text-white/60 mt-2">
              {event === 'win'
                ? 'Nucleus Accumbens · Big Win'
                : event === 'clutch'
                ? 'Executive Override · Resolving in 5s'
                : 'Limbic Threat Response · Loss'}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Desktop hint */}
      <div className="pointer-events-none absolute bottom-3 right-6 hidden lg:flex items-center gap-2.5 backdrop-blur-md bg-black/40 border border-white/5 rounded-full px-4 py-2">
        <MousePointer2 size={13} className="text-white/50" />
        <span className="font-data text-[10px] text-white/50 uppercase tracking-wider">Drag a brain to rotate · Scroll to zoom</span>
      </div>

      {/* Mobile / tablet drawer (< lg): all controls collapsed behind the menu */}
      <AnimatePresence>
        {!isDesktop && menuOpen && (
          <motion.div
            key="mobile-drawer"
            data-testid="mobile-drawer"
            initial={{ opacity: 0, y: '100%' }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: '100%' }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden pointer-events-auto fixed bottom-0 left-0 right-0 z-[60] max-h-[82vh] overflow-y-auto bg-black/90 backdrop-blur-2xl border-t border-white/15 rounded-t-3xl p-4 pb-6"
          >
            <div className="flex items-center justify-between mb-4">
              <span className="font-data text-[10px] tracking-[0.28em] uppercase text-white/45">Stress Controls</span>
              <button
                data-testid="mobile-drawer-close"
                onClick={() => setMenuOpen(false)}
                className="flex items-center justify-center w-9 h-9 bg-white/10 border border-white/15 rounded-lg"
              >
                <X size={16} />
              </button>
            </div>

            {/* View controls */}
            <div className="flex items-center gap-2 mb-4">
              <button
                data-testid="sync-toggle-mobile"
                onClick={toggleSync}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border border-white/15 font-data text-[10px] uppercase tracking-widest"
                style={{
                  backgroundColor: syncViews ? 'rgba(0,224,208,0.16)' : 'transparent',
                  color: syncViews ? '#00e0d0' : 'rgba(255,255,255,0.5)',
                }}
              >
                {syncViews ? <Link2 size={13} /> : <Link2Off size={13} />}
                {syncViews ? 'Synced' : 'Independent'}
              </button>
              <button
                data-testid="reset-views-button-mobile"
                onClick={onResetViews}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg border border-white/15 font-data text-[10px] uppercase tracking-widest text-white/70"
              >
                <RefreshCw size={12} /> Reset Views
              </button>
              <button
                data-testid="compare-toggle-mobile"
                onClick={onToggleCompare}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg border border-white/15 font-data text-[10px] uppercase tracking-widest"
                style={{
                  backgroundColor: compareMode ? 'rgba(255,215,0,0.16)' : 'transparent',
                  color: compareMode ? '#ffd700' : 'rgba(255,255,255,0.7)',
                }}
              >
                <Columns2 size={12} /> {compareMode ? 'Exit' : 'Compare'}
              </button>
            </div>

            <button
              data-testid="neuro-tour-button-mobile"
              onClick={onStartNeuroTour}
              className="w-full flex items-center justify-center gap-2 mb-4 px-3 py-2.5 rounded-lg border border-cyan-400/30 text-cyan-300 font-data text-[10px] uppercase tracking-widest active:scale-95 transition-transform"
            >
              <Compass size={13} /> Start Neuro-Tour
            </button>

            <LobeMeter />
            <div className="mb-3" />
            <StreakMeter />
            <div className="mb-4" />

            {renderControls()}

            <AnimatePresence>
              {vrMsg && (
                <div className="font-data text-[10px] uppercase tracking-widest text-amber-400/80 mt-4 text-center">{vrMsg}</div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
